import React, { FC, Key, useCallback, useEffect, useRef } from 'react';
import { Button, Table, Tooltip, Typography } from 'antd';
import { useMediaPredicate } from 'react-media-hook';
import { DataTable } from 'common/components/Table/DataTable';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { EUpdateStatus } from 'common/models/commonModel';
import { priceFormatter } from 'common/helpers/format.helper';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getPurchaseOrderRecoModel,
  setPurchaseOrderRecoModel,
  setPurchaseOrderRecoModelProducts,
} from 'app/store/reducers/purchaseOrder.reducer';
import { getGeneratePoConfig } from 'entities/PurchaseOrder/PurchaseOrder.helper';
import { IPurchaseOrderRecoProduct, IPurchaseOrderStoreModel } from 'entities/PurchaseOrder/PurchaseOrder.models';

const DRAFT_STORAGE_KEY = 'po-draft-order-counts';

type DraftOrderCounts = Record<string, number>;

const saveDraftToStorage = (storeCode: string, counts: DraftOrderCounts) => {
  try {
    const all = JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) || '{}');
    all[storeCode] = counts;
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // ignore storage errors
  }
};

const loadDraftFromStorage = (storeCode: string): DraftOrderCounts | null => {
  try {
    const all = JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) || '{}');
    return all[storeCode] || null;
  } catch {
    return null;
  }
};

export const clearDraftFromStorage = (storeCode: string) => {
  try {
    const all = JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) || '{}');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [storeCode]: _removed, ...rest } = all;
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(rest));
  } catch {
    // ignore storage errors
  }
};

interface IComponentProps {
  store?: IPurchaseOrderStoreModel;
  disabled?: boolean;
  disabledReason?: string;
  onConfirm: () => void;
  onViewRecoBasis: () => void;
}

// setTimeout overflows on >2^31 ms (~24.85 days) and fires immediately. Realistically
// max delta is ~7 days, but defensive max-clamp future-proofs schedule edges.
const MAX_TIMEOUT_MS = 2_147_483_647;

export const GeneratePOTable: FC<IComponentProps> = ({ store, disabled, disabledReason, onConfirm, onViewRecoBasis }) => {
  const dispatch = useAppDispatch();
  const { purchaseOrderRecoModel, purchaseOrderRecoModelProducts } = useAppSelector((state) => state.purchaseOrder);
  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  const cutoffAt = purchaseOrderRecoModel?.cutoffAt;

  // Effect 1: fetch RECO on store change (or reset when store cleared).
  // MUST NOT depend on cutoffAt — otherwise the very fetch that populates cutoffAt
  // re-triggers this effect → second redundant HTTP round-trip.
  useEffect(() => {
    if (!store) {
      dispatch(setPurchaseOrderRecoModel(null));
      return;
    }

    dispatch(getPurchaseOrderRecoModel({ storeCode: store.code as string, location: store.location }));
  }, [store]);

  // Effect 2: schedule a refetch at cutoffAt to roll RECO over to next delivery date.
  // Only schedules a timer; does NOT dispatch a fetch on mount (that's effect 1).
  useEffect(() => {
    if (!store || !cutoffAt) {
      return;
    }

    const delta = new Date(cutoffAt).getTime() - Date.now();
    if (delta <= 0) {
      // Race: cutoff already passed between fetch and effect run — single refetch
      // pulls fresh next-day RECO; no timer needed.
      dispatch(getPurchaseOrderRecoModel({ storeCode: store.code as string, location: store.location }));
      return;
    }

    // +1s buffer so server-side cutoff branch fires on next refetch.
    const safeDelta = Math.min(delta + 1000, MAX_TIMEOUT_MS);
    const timeoutId = setTimeout(() => {
      dispatch(getPurchaseOrderRecoModel({ storeCode: store.code as string, location: store.location }));
    }, safeDelta);

    return () => clearTimeout(timeoutId);
  }, [store, cutoffAt]);

  useEffect(() => {
    if (!purchaseOrderRecoModel) {
      dispatch(setPurchaseOrderRecoModelProducts([]));
      return;
    }

    const draft = store ? loadDraftFromStorage(String(store.code)) : null;

    const products = purchaseOrderRecoModel.products.map((item, index) => {
      return {
        ...item,
        key: index,
        orderCount: draft?.[item.prodCode] ?? item.recoCount,
        status: undefined,
      };
    });

    dispatch(setPurchaseOrderRecoModelProducts(products));
  }, [purchaseOrderRecoModel]);

  const config = getGeneratePoConfig(isMobile);
  const totalAmount = purchaseOrderRecoModelProducts.reduce((acc, item) => acc + item.orderCount * item.price, 0);

  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const persistDraft = useCallback(
    (products: IPurchaseOrderRecoProduct[]) => {
      if (!store) {
        return;
      }
      const counts: DraftOrderCounts = {};
      products.forEach((item) => {
        counts[item.prodCode] = item.orderCount;
      });
      saveDraftToStorage(String(store.code), counts);
    },
    [store]
  );

  const handleOrderCountChange = (key: Key, value: object) => {
    const updatedProducts = purchaseOrderRecoModelProducts.map((item, index) => {
      if (index == key) {
        return {
          ...item,
          ...value,
          status: EUpdateStatus.Saved,
        };
      }

      return item;
    });

    dispatch(setPurchaseOrderRecoModelProducts(updatedProducts));
    persistDraft(updatedProducts);

    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
    }

    savedTimerRef.current = setTimeout(() => {
      dispatch(
        setPurchaseOrderRecoModelProducts(
          updatedProducts.map((item) => (item.key === key ? { ...item, status: undefined } : item))
        )
      );
    }, 1500);
  };

  return (
    <DataTable<IPurchaseOrderRecoProduct>
      className="generate-po-table"
      dataSource={purchaseOrderRecoModelProducts}
      config={config}
      // MBC-80: spec — 10 visible rows + sticky header on Generate PO.
      scroll={{ y: 500 }}
      onFieldChange={handleOrderCountChange}
      summary={() => {
        if (!purchaseOrderRecoModelProducts.length) {
          return null;
        }

        return (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6} align="end">
                <Typography.Text
                  ellipsis
                  style={{
                    fontFamily: 'Poppins-Regular',
                    color: '#3359ff',
                    ...(isMobile && { display: 'flex', justifyContent: 'space-between' }),
                  }}
                >
                  <span>Order Total: </span>
                  <span>{priceFormatter(totalAmount)}</span>
                </Typography.Text>
              </Table.Summary.Cell>
              {!isMobile && <Table.Summary.Cell index={1} />}
            </Table.Summary.Row>
            {!isMobile && (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Button className="btn-secondary" onClick={onViewRecoBasis}>
                    View Reco Basis
                  </Button>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={3} align="end">
                  <Tooltip title={disabledReason ?? ''}>
                    <Button className="btn-primary" onClick={onConfirm} disabled={disabled}>
                      Confirm Order
                    </Button>
                  </Tooltip>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
              </Table.Summary.Row>
            )}
          </Table.Summary>
        );
      }}
    />
  );
};
